import {
  createHash,
  createDetachedSignature,
  execute,
} from 'crypto-pro';

import { getFileFromUrl } from '@/utils/base64';
// import { formatDateTime } from '@/utils/formatDate';
import SignatureFile from './signature-file';

function formatDate(date) {
  if (!date) return null;
  if (Array.isArray(date)) {
    return date.map((d) => {
      const [dateWithoutTime] = d.split(' ');
      return dateWithoutTime.split('-').reverse().join('.');
    });
  }
  const [dateWithoutTime] = date.split(' ');
  return dateWithoutTime;
}
function formatDateTime(value) {
  if (!value) return '-';
  const [date, time] = value.split(' ');
  const [h, i] = time.split(':');
  const dateformat = formatDate(date);
  return `${dateformat} ${h}:${i} `;
}

const ERRORS = {
  '0x800B0101': 'Истек/не наступил срок действия требуемого сертификата при проверке по системным часам или по отметке времени в подписанном файле.',
  '0x800B010C': 'Сертификат был отозван поставщиком этого сертификата.',
  '0x800B010A': 'Сертификат был отозван поставщиком этого сертификата.',
  '0x8009100E': 'Сертификат был отозван поставщиком этого сертификата.',
  '0x8009200E': 'Сертификат был отозван поставщиком этого сертификата.',
  '0x80091007': 'Неправильное значение хеша.',
};

const SUBJECT_FIELDS = {
  commonName: { key: 'CN', title: 'Полное наименование' },
  lastName: { key: 'SN', title: 'Фамилия' },
  firstMiddleName: { key: 'G', title: 'Имя Отчество' },
  title: { key: 'T', title: 'Должность' },
  orgUnit: { key: 'OU', title: 'Структурное подразделение' },
  organization: { key: 'O', title: 'Наименование Организации' },
  locality: { key: 'L', title: 'Наименование населенного пункта' },
  country: { key: 'C', title: 'Страна' },
  street: { key: 'STREET', title: 'Название улицы, номер дома, корпуса, строения, квартиры, помещения' },
  subjectFederation: { key: 'S', title: 'Субъект Федерации' },
  inn: { key: 'ИНН', title: 'ИНН' },
  linn: { key: 'ИНН ЮЛ', title: 'ИНН ЮЛ' },
  ogrn: { key: 'ОГРН', title: 'ОГРН' },
  ogrnip: { key: 'ОГРНИП', title: 'ОГРНИП' },
  snils: { key: 'СНИЛС', title: 'СНИЛС' },
  email: { key: 'E', title: 'Адрес электронной почты' },
};

const CERTIFICATE_FIELDS = {
  issuerName: { title: 'Выдан' },
  serialNumber: { title: 'Серийный номер сертификата' },
  validFromDate: { title: 'Дата начала действия сертификата' },
  validToDate: { title: 'Дата окончания действия сертификата' },
  signingTime: { title: 'Дата подписи документа' },
};

export default class Signature {
  constructor(file, signature, hash, signedAt) {
    this.file = Object
      .getPrototypeOf(file) === SignatureFile.prototype ? file : new SignatureFile(file);
    this.signature = signature;
    this.hash = hash;
    this.signed_at = signedAt;
    this.signData = {};
    this.signDataFields = [];

    this.signed = Boolean(signature);
    this.signing = false;

    this.sign = (certificate) => this.signFile(certificate);
    this.validate = () => this.getSignData();
    // this.downloadSignatureFile = (expansion) => this.downloadSignatureFileMethod(expansion);
  }

  signFile(certificate) {
    return new Promise((resolve, reject) => {
      this.signing = true;
      const createSignature = (message, hash) => {
        const { thumbprint } = certificate;
        return new Promise((resolve2, reject2) => {
          const signaturePromise = createDetachedSignature(thumbprint, hash);
          signaturePromise.then(async (signature) => {
            resolve2({ hash, signature });
          }, (error) => {
            reject2(error);
          });
        });
      };
      this.file.getArrayBuffer().then(
        (message) => {
          createHash(message).then(
            createSignature.bind(null, message),
            (hashError) => { reject(hashError); },
          )
            .then((res) => {
              this.signed = true;
              this.signature = res.signature;
              this.hash = res.hash;
              this.signed_at = new Date().toLocaleString('sv');
              resolve(this);
            })
            .catch((error) => { reject(error); })
            .finally(() => { this.signing = false; });
        },
        (error) => { reject(error); },
      ).catch((error) => { reject(error); });
    });
  }

  async getSignData() {
    if (this.signDataFields.length) {
      return new Promise((resolve) => {
        resolve({ fields: this.signDataFields, data: this.signData });
      });
    }
    return new Promise((resolve, reject) => {
      const { signature, file } = this;
      file.getBase64().then(
        ({ base64Content }) => {
          execute(async (utils) => {
            const { cadesplugin: cadesPlugin } = utils;
            const {
              CreateObjectAsync,
              getLastError, CADESCOM_CADES_BES, CADESCOM_BASE64_TO_BINARY,
            } = cadesPlugin;
            await cadesPlugin.async_spawn(
              function* (args) {
                const oSignedData = yield CreateObjectAsync('CAdESCOM.CadesSignedData');
                try {
                  yield oSignedData.propset_ContentEncoding(CADESCOM_BASE64_TO_BINARY);
                  yield oSignedData.propset_Content(base64Content);
                  yield oSignedData.VerifyCades(signature, CADESCOM_CADES_BES, true);
                } catch (error) {
                  const lastError = getLastError(error);
                  const lastErrorNumber = lastError.match(/\((0x[0-9a-fA-F]+)\)/)[1];
                  if (ERRORS[lastErrorNumber]) return args[0](oSignedData, ERRORS[lastErrorNumber]);
                  return args[1](lastError);
                }
                return args[0](oSignedData);
              },
              async (result, error) => {
                await this.serializeSingData(result, error);
                resolve({ fields: this.signDataFields, data: this.signData });
              },
              (error) => { reject(error); },
            );
          }).catch((error) => { reject(error); });
        },
        (error) => { reject(error); },
      ).catch((error) => { reject(error); });
    });
  }

  async serializeSingData(data) {
    const serializeInfo = (info) => {
      const pattern = /([^=,]+)=("[^"]+\b"|[^,]+)/g;
      const newKeys = Object.entries(SUBJECT_FIELDS).reduce((acc, [key, value]) => {
        acc[value.key] = key;
        return acc;
      }, {});
      return Object.values(info.match(pattern)).reduce((acc, field) => {
        const [key, value] = field.split('=');
        acc[newKeys[key.trim()]] = value.replace(/""/g, '"').replace(/^"|"$/g, '');
        return acc;
      }, {});
    };
    const formatSignatureDate = (date) => formatDateTime(new Date(date).toLocaleString('sv'));

    const serialize = async (val) => {
      let certificate = null;
      let signingTime = null;
      try {
        const signers = await val.Signers;
        const signer = await signers.Item(1);
        const time = await signer.SigningTime;
        signingTime = formatSignatureDate(time);
        certificate = await signer.Certificate;
      } catch (error) {
        const certificates = await val.Certificates;
        // signingTime = error.message;
        certificate = await certificates.Item(1);
      }
      const serialNumber = await certificate.SerialNumber;
      const issuerName = await certificate.IssuerName;
      const subjectName = await certificate.SubjectName;
      const validFromDate = await certificate.ValidFromDate;
      const validToDate = await certificate.ValidToDate;
      const subjectFields = serializeInfo(subjectName);
      const issuerFields = serializeInfo(issuerName);
      const obj = {
        ...subjectFields,
        issuerName: issuerFields.commonName,
        serialNumber,
        validFromDate: formatSignatureDate(validFromDate),
        validToDate: formatSignatureDate(validToDate),
        signingTime,
      };
      return obj;
    };
    this.signData = await serialize(data);
    this.getSignDataFields();
  }

  getSignDataFields() {
    const availableFields = { ...SUBJECT_FIELDS, ...CERTIFICATE_FIELDS };
    this.signDataFields = Object.entries(this.signData)
      .map(([key, value]) => ({ text: availableFields[key]?.title ?? `Неизвестное поле(${key})`, value }));
  }

  async getSignatureFile(filename = 'signature.sig') {
    const file = await getFileFromUrl(`data:application/octet-stream;base64,${this.signature}`, filename);
    return file;
  }

  async downloadSignatureFile() {
    const link = document.createElement('a');
    const file = await this.getSignatureFile();
    link.href = URL.createObjectURL(file);
    link.download = `${this.file.is.name}.sig`;
    link.click();
  }
}
